/**
 * CELL AURA — Ingredients pinned editorial stage (photo edition).
 * GSAP ScrollTrigger pins the stage; scroll progress maps to active principle index.
 * Background image crossfades between 9 photographs, each paired with one principle.
 *
 * Honors shopify-3d-experience rules:
 *  - register plugin once (shared with cell-aura-scroll.js)
 *  - prefers-reduced-motion gate disables pinning
 *  - cleans up on shopify:section:unload
 */
(function () {
  'use strict';

  var INSTANCES = [];

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function init(section) {
    if (!window.gsap || !window.ScrollTrigger) return;
    if (window.ScrollToPlugin && window.gsap.registerPlugin) {
      try { window.gsap.registerPlugin(window.ScrollToPlugin); } catch (e) {}
    }
    if (section.__stageInit) return;
    section.__stageInit = true;

    if (reducedMotion()) return; // CSS handles static vertical layout

    var principles  = Array.prototype.slice.call(section.querySelectorAll('[data-principle]'));
    var mediaImgs   = Array.prototype.slice.call(section.querySelectorAll('[data-media-index]'));
    var dots        = Array.prototype.slice.call(section.querySelectorAll('[data-dot]'));
    var tickerCurrent = section.querySelector('[data-ticker-current]');
    var stageOuter  = section.querySelector('.seed-ingredients__stage-outer');
    var stage       = section.querySelector('[data-stage]');
    if (!principles.length || !stageOuter || !stage) return;

    var total = principles.length;
    var currentIndex = -1;

    function setActive(idx) {
      idx = Math.max(0, Math.min(total - 1, idx));
      if (idx === currentIndex) return;
      currentIndex = idx;

      principles.forEach(function (el, i) {
        if (i === idx) { el.setAttribute('data-active', ''); el.setAttribute('aria-hidden', 'false'); }
        else           { el.removeAttribute('data-active'); el.setAttribute('aria-hidden', 'true'); }
      });
      mediaImgs.forEach(function (img, i) {
        if (i === idx) img.classList.add('is-active');
        else           img.classList.remove('is-active');
      });
      dots.forEach(function (d, i) {
        if (i === idx) d.setAttribute('data-active', '');
        else           d.removeAttribute('data-active');
      });
      if (tickerCurrent) tickerCurrent.textContent = ('0' + (idx + 1)).slice(-2);
    }

    setActive(0);

    var st = ScrollTrigger.create({
      trigger: stageOuter,
      start: 'top top',
      end: 'bottom bottom',
      pin: stage,
      pinSpacing: false,
      anticipatePin: 1,
      onUpdate: function (self) {
        // Hysteresis band around each segment boundary for calmer transitions
        var segment = 1 / total;
        var progress = self.progress;
        var raw = progress / segment;
        var idx = Math.round(raw - 0.5);
        if (idx < 0) idx = 0;
        if (idx >= total) idx = total - 1;
        setActive(idx);

        // Feather media opacity at entry/exit so the pinned stage doesn't "snap" into view
        stage.style.setProperty('--stage-media-opacity', '1');
      }
    });

    // Skip section buttons — direction-aware visibility
    var skipDown = section.querySelector('[data-skip="down"]');
    var skipUp = section.querySelector('[data-skip="up"]');
    if (skipDown && skipUp) {
      var lastY = window.pageYOffset;
      var direction = 'down';
      var inRange = false;

      function updateSkip() {
        var rect = stageOuter.getBoundingClientRect();
        var vh = window.innerHeight;
        // Consider "in range" when the pinned section occupies any part of viewport
        inRange = rect.top < vh && rect.bottom > 0;
        if (!inRange) {
          skipDown.removeAttribute('data-visible');
          skipUp.removeAttribute('data-visible');
          return;
        }
        if (direction === 'down') {
          skipDown.setAttribute('data-visible', '');
          skipUp.removeAttribute('data-visible');
        } else {
          skipUp.setAttribute('data-visible', '');
          skipDown.removeAttribute('data-visible');
        }
      }

      window.addEventListener('scroll', function () {
        var y = window.pageYOffset;
        if (Math.abs(y - lastY) > 2) {
          direction = y > lastY ? 'down' : 'up';
          lastY = y;
        }
        updateSkip();
      }, { passive: true });

      function smoothJump(targetY) {
        gsap.to(window, {
          scrollTo: { y: targetY, autoKill: false },
          duration: 1.4,
          ease: 'power3.inOut'
        });
      }

      skipDown.addEventListener('click', function () {
        var absTop = window.pageYOffset + stageOuter.getBoundingClientRect().top;
        var target = absTop + stageOuter.offsetHeight + 8;
        if (window.gsap && window.ScrollToPlugin) smoothJump(target);
        else window.scrollTo({ top: target, behavior: 'smooth' });
      });
      skipUp.addEventListener('click', function () {
        var absTop = window.pageYOffset + stageOuter.getBoundingClientRect().top;
        var target = absTop - window.innerHeight - 8;
        if (target < 0) target = 0;
        if (window.gsap && window.ScrollToPlugin) smoothJump(target);
        else window.scrollTo({ top: target, behavior: 'smooth' });
      });

      updateSkip();
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var jumpTo = parseInt(dot.getAttribute('data-jump'), 10) || 0;
        var rect = stageOuter.getBoundingClientRect();
        var absTop = window.pageYOffset + rect.top;
        var distance = stageOuter.offsetHeight;
        var targetY = absTop + (distance * (jumpTo + 0.5) / total);
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      });
    });

    INSTANCES.push({ section: section, st: st });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
    }
  }

  function destroyAll() {
    INSTANCES.forEach(function (inst) {
      try { inst.st && inst.st.kill(); } catch (e) {}
      if (inst.section) inst.section.__stageInit = false;
    });
    INSTANCES = [];
  }

  function boot() {
    document.querySelectorAll('[data-ingredients-stage]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    setTimeout(boot, 50);
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-ingredients-stage]')) {
      destroyAll();
      setTimeout(boot, 50);
    }
  });
  document.addEventListener('shopify:section:unload', destroyAll);
})();
